import InfoOutlined from "@mui/icons-material/InfoOutlined";
import { IconButton, Typography } from "@mui/material";
import { deepOrange, deepPurple, lime } from "@mui/material/colors";
import { Box, Stack, useTheme } from "@mui/system";
import { BigTooltip } from "components/BigTooltip";
import { BarChart } from "components/Dashboard/BarChart";
import ChartLegend from "components/Dashboard/ChartLegend";
import { DashboardTile } from "components/Dashboard/DashboardTile";
import { StatDisplay } from "components/StatDisplay";
import { prettyPrintLargeQuantity } from "encamp-shared/src/utils/prettyPrintUnits";
import { UnitType, WasteShipmentChartsQuery } from "generated-graphql/graphql";
import { useMemo } from "react";
import { formatNumber } from "util/formatNumber";

import { formatMonth } from "./utils";

type Props = {
  data: WasteShipmentChartsQuery["facilityWasteShipmentCharts"]["wasteShipmentsByMonth"];
};

export const WasteShipmentsByMonthGraph = ({ data }: Props) => {
  const theme = useTheme();

  const totalHazardousWaste = useMemo(() => {
    return (data?.totalHazardousAmount ?? 0) + (data?.totalAcutelyHazardousAmount ?? 0);
  }, [data]);

  return (
    <Stack direction={{ xs: "column", lg: "row" }} spacing={2}>
      <DashboardTile.ContentArea
        pl={{ xs: 0, lg: 2 }}
        gap={2}
        direction={{ xs: "row", lg: "column" }}
        sx={{ flex: 0.21 }}
      >
        <StatDisplay
          label="Total non-hazardous waste"
          value={prettyPrintLargeQuantity(
            data?.totalNonhazardousAmount ?? 0,
            UnitType.Pounds,
            true
          )}
        />
        <StatDisplay
          label="Total hazardous waste"
          value={prettyPrintLargeQuantity(totalHazardousWaste, UnitType.Pounds, true)}
        />
        <StatDisplay
          label="Total state waste"
          value={prettyPrintLargeQuantity(
            data?.totalStateRegulatedAmount ?? 0,
            UnitType.Pounds,
            true
          )}
        />
      </DashboardTile.ContentArea>
      <DashboardTile.ContentArea sx={{ flex: 1 }}>
        {/* Title */}
        <Stack direction="row" alignItems="center">
          <Typography>Waste shipments by month in lbs</Typography>
          <BigTooltip
            title={
              <Typography variant="caption">
                Based on data ingested by Encamp, see how your historical waste shipments are broken
                down by classification. If both vendor and e‑Manifest data is present for a
                manifest, we will display vendor data.
              </Typography>
            }
          >
            <IconButton size="small">
              <InfoOutlined color="disabled" />
            </IconButton>
          </BigTooltip>
        </Stack>

        {/* Chart */}
        <Box height={theme.spacing(34)}>
          <HazardousWasteByMonthLegend />
          <WasteShipmentsByMonthChart data={data} />
        </Box>
      </DashboardTile.ContentArea>
    </Stack>
  );
};

const WasteShipmentsByMonthChart = ({ data }: Props) => {
  const dataset = useMemo(() => {
    return (
      data?.byMonth.map((monthData, index) => ({
        month: formatMonth(monthData.month, index === 0),
        nonHazardous: monthData.unifiedNonhazardousAmount,
        hazardous: monthData.unifiedHazardousAmount + monthData.unifiedAcutelyHazardousAmount,
        state: monthData.unifiedStateRegulatedAmount,
      })) ?? []
    );
  }, [data]);

  return (
    <BarChart
      dataset={dataset.length ? dataset : [{ amount: 0, label: "No data" }]}
      series={[
        {
          type: "bar",
          dataKey: "nonHazardous",
          color: lime[800],
          valueFormatter: (value) => ` Non-hazardous Waste (${formatNumber(value)} lbs)`,
        },
        {
          type: "bar",
          dataKey: "hazardous",
          color: deepOrange[300],
          valueFormatter: (value) => `Hazardous Waste (${formatNumber(value)} lbs)`,
        },
        {
          type: "bar",
          dataKey: "state",
          color: deepPurple[300],
          valueFormatter: (value) => `State Regulated Waste (${formatNumber(value)} lbs)`,
        },
      ]}
      xAxis={[
        {
          scaleType: "band",
          dataKey: "month",
          categoryGapRatio: 0.4,
          barGapRatio: 0.4,
        },
      ]}
      yAxis={[{ scaleType: "sqrt" }]}
    />
  );
};

function HazardousWasteByMonthLegend() {
  return (
    <ChartLegend
      config={{ direction: "row", position: "above" }}
      items={[
        {
          color: lime[800],
          value: `Non-hazardous Waste`,
          tooltip: "Waste that causes no harm to human or environmental health",
        },
        {
          color: deepOrange[300],
          value: `Hazardous Waste`,
          tooltip:
            "Waste that is flammable, corrosive, reactive, or toxic (D-codes), or is listed on one of the EPA's four lists of hazardous waste (F, K, U, or P codes)",
        },
        {
          color: deepPurple[300],
          value: `State Regulated Waste`,
          tooltip: "Waste classified as hazardous by the facility's state.",
        },
      ]}
      sx={{ paddingLeft: 6, position: "absolute" }}
    />
  );
}
